/**
 * A text field: a label, the field, and a line under it — a hint, or what is
 * wrong.
 *
 * The field is a dark well with a hairline and, along its foot, an underline
 * in the caption ink: the hairline is Vesper's, the underline is what makes
 * the field findable at the 3:1 WCAG asks of a control's edge. Focus draws the
 * one focus ring; an error turns the hairline and the message coral and ties
 * the message to the field. `prefix` and `suffix` set a unit either side of
 * the value: "$", "km". `multiline` makes it a field of several lines, `rows`
 * tall and growing no further — an answer in the player's own words. `code`
 * sets the value as a code is read out — the label face, its characters
 * spaced as a ticker's: a team code. `count` ends the line under the field —
 * a name's letters against its most — and stays when an error takes the
 * hint's place.
 *
 *   <TextField label="Configuration name" value={name} onChange={(e) => setName(e.target.value)} />
 *   <TextField label="Save ID" placeholder="Save ID (auto if empty)" hint="Leave empty for an auto-numbered ID" />
 *   <TextField multiline rows={4} label="Your answer" value={text} onChange={(e) => setText(e.target.value)} />
 *   <TextField code label="Team code" inputMode="numeric" value={code} onChange={…} />
 *   <TextField label="Your name" hint="Up to 12 characters" count="3 / 12" value={name} onChange={…} />
 */

import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react';

import styles from './text-field.module.css';
import utilities from '../../styles/utilities.module.css';

interface FieldParts {
  label: ReactNode;
  /** A quiet line under the field: what goes in it. */
  hint?: ReactNode;
  /** What is wrong with the value; replaces the hint and marks the field invalid. */
  error?: ReactNode;
  /** At the end of the line under the field, kept beside an error: "3 / 12". */
  count?: ReactNode;
}

type TextFieldProps = FieldParts &
  (
    | (Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> & {
        multiline?: false;
        prefix?: ReactNode;
        suffix?: ReactNode;
        /** A code: the label face, its characters spaced as a ticker's. */
        code?: boolean;
      })
    | (TextareaHTMLAttributes<HTMLTextAreaElement> & {
        /** Several lines, `rows` tall. */
        multiline: true;
      })
  );

export function TextField(props: TextFieldProps) {
  const ownId = useId();
  const fieldId = props.id ?? ownId;
  const messageId = `${fieldId}-message`;
  const message = props.error ?? props.hint;
  // The field's message, and whatever else the caller says describes it: a
  // free answer's minimum, counted by a meter beside it.
  const described = {
    id: fieldId,
    'aria-invalid': props.error ? true : props['aria-invalid'],
    'aria-describedby': [message ? messageId : undefined, props['aria-describedby']].filter(Boolean).join(' ') || undefined,
  } as const;

  let field: ReactNode;
  if (props.multiline) {
    // The field's own parts are taken off, so only the textarea's reach it.
    const { label: _label, hint: _hint, error, count: _count, multiline: _multiline, className: _className, ...area } = props;
    field = (
      <div className={[styles.box, styles.lines, error ? styles.invalid : ''].filter(Boolean).join(' ')}>
        <textarea {...area} {...described} className={[styles.input, styles.area].join(' ')} />
      </div>
    );
  } else {
    // The field's own parts are taken off, so only the input's reach it.
    const { label: _label, hint: _hint, error, count: _count, multiline: _multiline, prefix, suffix, code, className: _className, ...input } = props;
    field = (
      <div className={[styles.box, error ? styles.invalid : ''].filter(Boolean).join(' ')}>
        {prefix && <span className={styles.affix}>{prefix}</span>}
        <input {...input} {...described} className={code ? `${styles.input} ${styles.code}` : styles.input} />
        {suffix && <span className={styles.affix}>{suffix}</span>}
      </div>
    );
  }

  return (
    <div className={[utilities.fieldStack, props.className].filter(Boolean).join(' ')}>
      <label htmlFor={fieldId} className={utilities.fieldLabel}>
        {props.label}
      </label>
      {field}
      {(message || props.count != null) && (
        <div className={styles.under}>
          {message && (
            <p id={messageId} className={props.error ? styles.error : styles.hint}>
              {message}
            </p>
          )}
          {props.count != null && (
            <span className={styles.count} aria-hidden="true">
              {props.count}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
